import {
  Card,
  CircularProgress,
  Dialog,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from "@material-ui/core";
import React, { useCallback, useState } from "react";
import { Form } from "react-final-form";
import {
  Button,
  CardContentInner,
  FormInput,
  Record,
  TextInput,
  useNotify,
  useTranslate,
} from "react-admin";
import { CompactFormToolbar } from "../../common/form/CompactFormToolbar";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import SaveIcon from "@material-ui/icons/Save";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import {
  ChangePasswordInputDto,
  DriverApiFactory,
} from "../../../common/data/service-proxies";
import { handleAxiosError } from "../../../common/data/axiosErrorHandler";

interface Props<T> {
  record?: T & Record;
  onClick?: (e: any) => void;
}

export const ChangeDriverPasswordAction = <T extends {} = Record>({
  record,
  onClick,
}: Props<T>) => {
  const translate = useTranslate();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const axios = useAxiosInstance();
  const notify = useNotify();
  const [loading, setLoading] = useState<boolean>(false);

  const handleFormSubmit = useCallback(
    (data: ChangePasswordInputDto) => {
      if (!axios) {
        return;
      }

      if (!record?.id) {
        return;
      }

      setLoading(true);
      DriverApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppDriverChangepasswordPost({
          ...data,
          driverId: Number(record.id),
        })
        .then(() =>
          notify("resources.admin-drivers.notifications.passwordChanged", {
            type: "info",
          })
        )
        .catch((e) => {
          notify("resources.admin-drivers.notifications.passwordChangeFailed", {
            type: "error",
          });
          console.error(
            "Unable to change the driver password",
            handleAxiosError(e)
          );
        })
        .finally(() => {
          setLoading(false);
          setDialogOpen(false);
        });
    },
    [record, axios, notify]
  );

  return (
    <>
      <MenuItem
        className="actions-menu-item"
        onClick={(e) => {
          setDialogOpen(true);
          if (typeof onClick === "function") {
            onClick(e);
          }
        }}
      >
        <ListItemIcon>
          <LockOpenIcon />
        </ListItemIcon>
        <ListItemText>
          {translate("resources.admin-drivers.labels.changePassword")}
        </ListItemText>
      </MenuItem>
      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (!loading) {
            setDialogOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {translate("resources.admin-drivers.labels.changePasswordTitle", {
            driver: record?.driverName,
          })}
        </DialogTitle>
        <Divider />
        <Card>
          <Form
            onSubmit={handleFormSubmit}
            render={({ handleSubmit }) => {
              return (
                <div className="simple-form form-create-edit-drawer">
                  <form onSubmit={handleSubmit}>
                    <CardContentInner>
                      <FormInput
                        basePath=""
                        input={
                          <TextInput
                            label="resources.admin-drivers.labels.newPassword"
                            source="password"
                            type="password"
                          />
                        }
                        record={record as Record}
                        resource=""
                        variant="outlined"
                      />
                    </CardContentInner>
                    <CompactFormToolbar>
                      <Button
                        variant="contained"
                        type="submit"
                        color="primary"
                        label="ra.action.save"
                      >
                        {loading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <SaveIcon />
                        )}
                      </Button>
                    </CompactFormToolbar>
                  </form>
                </div>
              );
            }}
          />
        </Card>
      </Dialog>
    </>
  );
};
